import { Platform } from "react-native";

import { logAdminAction } from "./auditLog";

/**
 * One-button CSV export for admin list pages. Web-only (admin portal
 * is web-only); on native we no-op + warn since there's no obvious
 * download surface.
 *
 * Usage:
 *   exportRowsAsCsv({
 *     filename: "students.csv",
 *     columns: [
 *       { header: "Name", get: (r) => r.full_name ?? "" },
 *       { header: "Email", get: (r) => r.email ?? "" },
 *     ],
 *     rows,
 *     auditPage: "students",
 *   });
 *
 * Respect the active filter: pass the FILTERED rows, not the raw set.
 *
 * Each export writes an audit-log row tagged `export.csv`.
 */

export type CsvColumn<T> = {
  header: string;
  get: (row: T) => string | number | null | undefined;
};

export type ExportInput<T> = {
  filename: string;
  columns: CsvColumn<T>[];
  rows: T[];
  /** Short identifier for the audit log entry, e.g. "students". */
  auditPage: string;
};

function escapeCell(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (s.length === 0) return "";
  const needsQuote = s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r");
  if (!needsQuote) return s;
  return `"${s.replace(/"/g, '""')}"`;
}

export function rowsToCsv<T>(columns: CsvColumn<T>[], rows: T[]): string {
  const lines: string[] = [];
  lines.push(columns.map((c) => escapeCell(c.header)).join(","));
  for (const r of rows) {
    lines.push(columns.map((c) => escapeCell(c.get(r))).join(","));
  }
  return lines.join("\r\n");
}

export function exportRowsAsCsv<T>(input: ExportInput<T>): void {
  const csv = rowsToCsv(input.columns, input.rows);

  if (Platform.OS === "web" && typeof window !== "undefined" && typeof document !== "undefined") {
    // BOM for Excel compatibility on Windows
    const blob = new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = input.filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  } else {
    console.warn("[csvExport] download is web-only; rows skipped on native:", input.rows.length);
  }

  void logAdminAction({
    action: "export.csv",
    entityType: "csv_export",
    summary: `Exported ${input.rows.length} rows from ${input.auditPage}`,
    diff: { after: { filename: input.filename, count: input.rows.length } },
  });
}

/** Eastern-TZ ISO date for inclusion in filename suffixes, e.g. "2026-05-08". */
export function todayDateForFilename(): string {
  const d = new Date();
  return d
    .toLocaleDateString("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/")
    .map((p) => p.padStart(2, "0"))
    .join("-");
}

/** Format an ISO timestamp for an exported cell — Eastern, ISO-ish for spreadsheet sortability. */
export function fmtCellDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
